import style from './style.module.css';
import { FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router';
import {
  PopConfirm,
  Button,
  Col,
  Row,
  Notification,
  UploadFile,
} from 'fave-ui';
import Form from '../../../../components/common/form/Form';
import { Input } from '../../../../components/common/form/Input';
import SubmitButton from '../../../../components/common/form/SubmitButton';
import { PageHeaderWithBackButton } from '../../../../components/common/PageHeaderWithBackButton';
import {
  AddCashbackCampaignPayload,
  useEditEcardCampaignMutation,
  useFetchCashbackCampaignListingData,
} from '../../../../services/CashbackCampaign/useFetchFLPCashbackListing';
import { cashbackCampaignTabs } from '../..';
import { DatePicker } from '../../../../components/common/form';
import ImageUpload from '../../../../components/common/form/ImageUpload';
import { appendFormData, formatDate } from '../../../../utils/utilFunctions';

const CAMPAIGN_TYPE = 'BKE::ECard';

const ECardCampaignFormSchema = Yup.object().shape({
  campaign_type: Yup.string().required(
    'Please select a campaign type to continue',
  ),
  campaign_name: Yup.string().required('Please give your campaign a name'),
  start_date: Yup.date()
    .typeError('Please select a start date')
    .required('Please select a start date'),
  end_date: Yup.date()
    .typeError('Please select a end date')
    .min(Yup.ref('start_date'), "End date can't be before start date")
    .required('Please select a end date'),
  description: Yup.string()
    .required('Please enter a description')
    .max(50, 'Description must be less than 50 characters'),
  image: Yup.array()
    .of(Yup.mixed().nullable().required('Image is required'))
    .min(1, 'Please upload an image'),
});

const successNotification = () => {
  Notification.success({
    message: 'Campaign Successfully Edited!',
    description:
      'Your BKE eCard Giveaway funded cashback campaign has been edited successfully',
  });
};

export default function EditEcardCashbackCampaign() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: cashbackCampaignData } = useFetchCashbackCampaignListingData({
    id: id,
  });

  const { mutateAsync: editCampaign } = useEditEcardCampaignMutation();

  const campaignTab = cashbackCampaignTabs.find(
    n => n.funder_type === CAMPAIGN_TYPE,
  )?.key;

  const redirectUrl = `/admin/merchandise_and_campaign/cashback_campaigns${campaignTab ? `?tab=${campaignTab}` : ''}`;

  const initialValues: AddCashbackCampaignPayload = {
    campaign_type: cashbackCampaignData?.campaign_type ?? '',
    campaign_name: cashbackCampaignData?.campaign_name ?? '',
    description: cashbackCampaignData?.description ?? '',
    start_date: cashbackCampaignData?.start_date,
    end_date: cashbackCampaignData?.end_date,
    image: [
      {
        uid: '-1',
        name: 'image.png',
        status: 'done',
        url: cashbackCampaignData?.image_url,
      },
    ],
  };

  const handleSubmit = async (
    formValues: AddCashbackCampaignPayload,
    { setSubmitting }: FormikHelpers<AddCashbackCampaignPayload>,
  ) => {
    const payload = {
      ...formValues,
      id: id,
      start_date: formatDate(formValues.start_date, 'yyyy-MM-d'),
      end_date: formatDate(formValues.end_date, 'yyyy-MM-d'),
      image:
        formValues.image &&
        formValues.image.length > 0 &&
        formValues.image[0].originFileObj != undefined
          ? formValues.image[0].originFileObj
          : [],
    };
    const formData = appendFormData(payload);

    await editCampaign(
      { id: payload.id, formData: formData },
      {
        onSuccess: () => {
          successNotification();
          navigate(redirectUrl);
        },
        onSettled: () => {
          setSubmitting(false);
        },
      },
    );
  };

  const defaultDisplayImgList =
    cashbackCampaignData && cashbackCampaignData?.image_url !== undefined
      ? [{ thumbUrl: cashbackCampaignData.image_url }]
      : [];

  return (
    <>
      <PageHeaderWithBackButton
        title={`Edit eCard Giveaway Cashback Campaign`}
      />
      <Form
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={ECardCampaignFormSchema}
        validateOnMount
        validateOnChange
        validateOnSchemaChange
      >
        <Row gutter={[16, 16]}>
          <p className={style.section}>1. Campaign Type</p>
          <Col span={24}>
            <Input
              name="campaign_type"
              label="Campaign Type"
              bordered
              disabled
              style={{ marginBottom: 0 }}
            />
          </Col>
          <p className={style.section}>2. Campaign Detail</p>

          <Col span={24}>
            <Input
              name="campaign_name"
              label="Campaign Name"
              bordered
              disabled
              style={{ marginBottom: 0 }}
            />
          </Col>
          <Col span={24}>
            <DatePicker
              name="start_date"
              label="Start Date"
              allowClear
              bordered
            />
          </Col>

          <Col span={24}>
            <DatePicker name="end_date" label="End Date" allowClear bordered />
          </Col>

          <Col span={24}>
            <Input
              type="textArea"
              name="description"
              label="Description"
              bordered
              style={{ marginBottom: 0 }}
            />
          </Col>

          <Col span={24}>
            <ImageUpload
              name="image"
              label="Upload campaign masthead - PNG (120px x 44px)"
              types={['image/png']}
              imgNumber={1}
              defaultDisplayImgList={defaultDisplayImgList as UploadFile<{}>[]}
            />
          </Col>

          <Col span={24}>
            <SubmitButton disableIfNotValid disableIfNotDirty>
              {(_, isSubmitting) => (isSubmitting ? 'Submitting...' : 'Save')}
            </SubmitButton>
            <PopConfirm
              title="Are you sure you want to cancel? All progress will be lost for the current campaign."
              onConfirm={() => navigate(redirectUrl)}
              okText="Yes, I am sure"
              cancelText="Go Back"
            >
              <Button type="primary" size="large" className={style.cancelBtn}>
                Cancel
              </Button>
            </PopConfirm>
          </Col>
        </Row>
      </Form>
    </>
  );
}
